import React from 'react'
import { graphql, StaticQuery, Link } from 'gatsby'

import { linkResolver } from '../../util/linkResolver'
import { NpfLogo } from '../Icons'
import SocialIconRow from '../SocialIconRow'
import * as Styled from './styles/ExpandedMenu.styles'
import { NAV_MENU_STATUS } from '../../util/constants'
import SearchMenu from './SearchMenu'

const NAV_QUERY = graphql`
  {
    prismicGeneralSiteSettings {
      data {
        helpline_phone_number
        donate_url {
          id
          uid
          type
          link_type
          target
          url
        }
      }
    }
    prismicNavigationMenu {
      data {
        body {
          ... on PrismicNavigationMenuDataBodySection {
            id
            slice_label
            slice_type
            primary {
              external_link_text
              parent {
                uid
                type
                link_type
                id
                url
                document {
                  ... on PrismicLandingPage {
                    data {
                      page_title {
                        text
                      }
                    }
                  }
                  ... on PrismicAdvanceLandingPage {
                    data {
                      page_title {
                        text
                      }
                    }
                  }
                }
              }
            }
            items {
              external_link_text
              subpage {
                uid
                type
                link_type
                id
                url
                document {
                  ... on PrismicSubpage {
                    data {
                      title {
                        text
                      }
                    }
                  }
                  ... on PrismicCmeMediaLibraryPage {
                    data {
                      title {
                        text
                      }
                    }
                  }
                  ... on PrismicMediaLibraryPage {
                    data {
                      title {
                        text
                      }
                    }
                  }
                  ... on PrismicHcpdLandingPage {
                    data {
                      title {
                        text
                      }
                    }
                  }
                  ... on PrismicProductLandingPage {
                    data {
                      title {
                        text
                      }
                    }
                  }
                  ... on PrismicAudienceCampaignPage {
                    data {
                      title: hero_text {
                        text
                      }
                    }
                  }
                  ... on PrismicMediaLibraryItem {
                    data {
                      title {
                        text
                      }
                    }
                  }
                  ... on PrismicHcpdNewRegistrationPage {
                    data {
                      title {
                        text
                      }
                    }
                  }
                  ... on PrismicAdvanceArticle {
                    data {
                      title: header {
                        text
                      }
                    }
                  }
                  ... on PrismicStaffBoardLandingPage {
                    data {
                      title {
                        text
                      }
                    }
                  }
                  ... on PrismicNpfAuthor {
                    data {
                      title: name {
                        text
                      }
                    }
                  }
                  ... on PrismicEventsLandingPage {
                    data {
                      title {
                        text
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`
const ParentLinkInternal = ({ node, route }) => {
  if (!node) {
    return null
  }
  return (
    <Styled.LandingPageLink
      to={linkResolver(node.primary.parent)}
      $isActivePage={node.primary.parent.uid === route}
    >
      {node.primary.parent.document.data.page_title.text}
    </Styled.LandingPageLink>
  )
}

const ParentLinkExternal = ({ node }) => {
  if (!node) {
    return null
  }
  return (
    <Styled.LandingPageLink
      as="a"
      href={node.primary.parent.url}
      target={node.primary.parent.target === '_blank' ? '_blank' : '_self'}
      rel={node.primary.parent.target === '_blank' ? 'noreferrer' : ''}
    >
      {node.primary?.external_link_text}
    </Styled.LandingPageLink>
  )
}

const ParentLinkItem = ({ node, route }) => {
  if (!node) {
    return null
  }
  if (
    node.primary.parent.link_type === 'Web' ||
    node.primary.parent.link_type === 'File'
  ) {
    return <ParentLinkExternal node={node} />
  } else {
    if (!node.primary?.parent?.uid) {
      return null
    } // only link to valid page types
    return <ParentLinkInternal node={node} route={route} />
  }
}

const ChildLinkInternal = ({ field, route }) => {
  if (!field) {
    return null
  }
  return (
    <Styled.SubpageLink
      to={linkResolver(field.subpage)}
      $isActivePage={field.subpage.uid === route}
    >
      {field.subpage.document.data.title.text}
    </Styled.SubpageLink>
  )
}

const ChildLinkExternal = ({ field }) => {
  if (!field) {
    return null
  }
  return (
    <Styled.SubpageLink
      as="a"
      href={field.subpage.url}
      target={field.subpage.target === '_blank' ? '_blank' : '_self'}
      rel={field.subpage.target === '_blank' ? 'noreferrer' : ''}
    >
      {field.external_link_text}
    </Styled.SubpageLink>
  )
}

const ChildLinkItem = ({ field, route }) => {
  if (!field) {
    return null
  }
  if (field.subpage.link_type === 'Web' || field.subpage.link_type === 'File') {
    return <ChildLinkExternal field={field} />
  } else {
    if (!field.subpage?.uid) {
      return null
    } // only link to valid page types
    return <ChildLinkInternal field={field} route={route} />
  }
}

const NavMenu = ({ isNavMenuOpen }) => (
  <StaticQuery
    query={`${NAV_QUERY}`}
    render={data => {
      // TODO: This pattern will not work when paths get more than one level deep...
      const url =
        typeof window !== 'undefined' ? window.location.href.split('/') : ''
      const route = url[url.length - 1]
      const settings = data.prismicGeneralSiteSettings.data

      return (
        <Styled.NavContainer>
          <Styled.PagesList isNavMenuOpen={isNavMenuOpen}>
            {data.prismicNavigationMenu.data.body.map(node => (
              <React.Fragment key={node.id}>
                <ParentLinkItem node={node} route={route} />
                {node.items.map(field => (
                  <ChildLinkItem
                    key={
                      field.subpage.id ? field.subpage.id : field.subpage.url
                    }
                    field={field}
                    route={route}
                  />
                ))}
              </React.Fragment>
            ))}
          </Styled.PagesList>
          <Styled.DonateBlock isNavMenuOpen={isNavMenuOpen}>
            <Styled.DonateButton to={linkResolver(settings.donate_url)}>
              Donate
            </Styled.DonateButton>
            <Styled.Helpline>
              Helpline:&nbsp;
              <a href={`tel:${settings.helpline_phone_number}`}>
                {settings.helpline_phone_number
                  ? settings.helpline_phone_number
                  : '800-723-9166'}
              </a>
            </Styled.Helpline>
            <Styled.FollowUs>Follow Us On Social Media</Styled.FollowUs>
            <SocialIconRow />
            <Link to="/">
              <NpfLogo isFilledWhite />
            </Link>
          </Styled.DonateBlock>
        </Styled.NavContainer>
      )
    }}
  />
)

const ExpandedMenu = ({ navMenuStatus, isNavMenuOpen, setNavMenuStatus }) => (
  <Styled.ExpandedMenu isNavMenuOpen={isNavMenuOpen}>
    {navMenuStatus === NAV_MENU_STATUS.OPEN_NAV && (
      <NavMenu isNavMenuOpen={isNavMenuOpen} />
    )}
    {navMenuStatus === NAV_MENU_STATUS.OPEN_SEARCH && (
      <SearchMenu
        isSearchMenuOpen={isNavMenuOpen}
        setNavMenuStatus={setNavMenuStatus}
      />
    )}
  </Styled.ExpandedMenu>
)

export default ExpandedMenu
